import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { SButton } from '@chargebee/sting-react';
import { useShowDemoData } from '@bbkAdminComponents/play/play-context';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { CreatePlayModal } from '@bbkAdminComponents/unified-plays/create-play-modal';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { useGetDemoSuffix } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectReportFilterForPicker } from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { PlayType } from '@bbkAdminRedux/rtkq/play.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type PredictiveDashboardMetricsData,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { cs } from '@bbkAdminUtils/css-utils';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { CBRIcons, Icons } from '@bbkAdminUtils/icons';
import { useUrl } from '@bbkAdminUtils/nav';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { useHistory, useLocation } from '@bbkShared/react-router';
import { cva } from '@bbkShared/utils/css-utils';
import { riskClassMap, RiskLevel } from './customers-preview';
import CardContentArea from '../cards/sections/card-content-area';
import { PredictiveBrandFilterPicker } from '../reports-filter-picker/reports-filter-picker';

type RiskIndicatorProps = {
  type: RiskLevel;
  count: number;
  showDownArrowFlag: boolean;
  onClick: (type: RiskLevel) => void;
};

type MetricCardProps = {
  title: string;
  value: number;
  maxValue: number;
  subtext: string;
  change: number;
  icon: React.ReactNode;
  isCurrency?: boolean;
  previousRefreshedDate?: string;
};
export type PredictiveMetric = {
  churn_risk_category: string;
  average_churn_risk_score: {
    value: number;
    change_percent: number;
  };
  customer_count: {
    value: number;
    change_percent: number;
  };
  mrr_impact: {
    value: number;
    change_percent: number;
  };
  range?: string;
};
const getIndicatorStyles = cva({
  base: 'tw-mb-3 tw-flex tw-gap-1 tw-rounded tw-py-2.5 tw-pr-4',
  variants: {
    riskLevel: {
      [RiskLevel.High]: 'tw-z-10 tw-bg-cbrdanger-200',
      [RiskLevel.Medium]: 'tw-z-10 tw-bg-cbrwarn-200',
      [RiskLevel.Low]: 'tw-z-10 tw-bg-cbryellow-200',
    },
    border: {
      true: 'tw-border tw-border-solid',
      false: '',
    },
  },
  defaultVariants: {
    riskLevel: RiskLevel.High,
    border: false,
  },
});
const RiskIndicator: React.FC<RiskIndicatorProps> = ({
  type,
  count,
  showDownArrowFlag,
  onClick,
}) => {
  const selectType = () => {
    onClick(type);
  };
  const styles = getIndicatorStyles({
    riskLevel: type,
    border: !showDownArrowFlag,
  });

  return (
    <div
      className={cs('tw-relative tw-inline-block tw-h-11', type, {
        'active_tab tw-translate-y-3.5': showDownArrowFlag,
      })}
      onClick={selectType}
    >
      <div
        className={cs(
          'tw-relative tw-flex tw-h-full tw-w-56 tw-items-center tw-justify-center tw-px-2.5 tw-py-2.5 tw-duration-[20]',
          {
            'tw-rounded-tl-3 tw-rounded-tr-3 tw-z-100': showDownArrowFlag,
          },
          styles
        )}
      >
        {type === RiskLevel.High && (
          <span
            className={cs('tw-mr-2 tw-flex tw-p-1', {
              '-tw-translate-y-2': showDownArrowFlag,
            })}
          >
            {CBRIcons.warningShield}
          </span>
        )}
        <div className={cs({ '-tw-translate-y-2': showDownArrowFlag })}>
          {type}
        </div>
        {count >= 0 && (
          <span className={cs({ '-tw-translate-y-2': showDownArrowFlag })} />
        )}
        {!showDownArrowFlag && (
          <span className="tw-m-2 tw-flex">{Icons.downArrow}</span>
        )}
      </div>
    </div>
  );
};
export const formatCurrencyOrNumber = (val: number, isCurrency?: boolean) =>
  isCurrency
    ? val.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })
    : val.toLocaleString('en-US', { maximumFractionDigits: 0 });

export const getIconDetails = (change: number) => {
  if (change > 0) {
    return {
      icon: CBRIcons.topPointingTriangle,
      class: 'tw-text-cbrdanger-500',
    };
  }
  return {
    icon: CBRIcons.downPointingTriangle,
    class: 'tw-text-cbrsuccess-500',
  };
};
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  maxValue,
  subtext,
  change,
  icon,
  isCurrency = false,
  previousRefreshedDate,
}) => {
  const { icon: changeIcon, class: iconClass } = getIconDetails(change);

  const renderChangeDateRow = () =>
    previousRefreshedDate ? (
      <>
        <span
          className={`tw-inline-flex tw-w-4 tw-items-center tw-justify-center tw-object-contain ${iconClass}`}
        >
          {changeIcon}
        </span>
        <span className="tw-flex tw-font-bold">{Math.abs(change)}%</span>
        <span className="tw-ml-1 tw-flex tw-text-xs tw-text-cbrneutral-400">
          (vs {previousRefreshedDate} )
        </span>
      </>
    ) : (
      <span className="tw-ml-1 tw-flex tw-text-xs tw-text-cbrneutral-400">
        -
      </span>
    );

  return (
    <div className="tw-m-2 tw-flex tw-basis-1/4 tw-flex-col tw-items-start tw-rounded-lg tw-bg-white tw-p-3 tw-py-2.5 tw-text-black">
      <div className="tw-flex tw-gap-1 tw-text-sm">
        <span className="tw-mr-1 tw-w-5 tw-pl-1">{icon}</span>
        <div className="tw-basis-auto">{title}</div>
      </div>
      <div className="tw-mt-2 tw-flex">
        <span className="tw-text-xl">
          {formatCurrencyOrNumber(value, isCurrency)}
        </span>
        <span className="tw-mt-1 tw-text-sm tw-font-semibold">
          /{formatCurrencyOrNumber(maxValue, isCurrency)}
        </span>
        <span className="tw-ml-1 tw-mt-1 tw-text-sm tw-font-semibold tw-text-cbrneutral-400">
          {subtext}
        </span>
      </div>
      <div className="tw-mt-2 tw-flex tw-gap-1 tw-text-xs tw-font-semibold">
        <div className="tw-flex tw-basis-auto">{renderChangeDateRow()}</div>
      </div>
    </div>
  );
};

export const RiskDashboard: React.FC = () => {
  const location = useLocation<{ riskCategory?: RiskLevel }>();
  const [riskCategory, setRiskCategory] = useState<RiskLevel>(RiskLevel.High);

  useEffect(() => {
    if (location.state?.riskCategory) {
      setRiskCategory(location?.state?.riskCategory);
    }
  }, [location.state]);

  const appKey = useSelector(selectAppId);
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;
  const deflectLicense = productLicense.deflectLicense;
  const agsAndDeflectLicense =
    (agsLicense && deflectLicense) || growthV2License;
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);
  const isMultiBrand = useFeatureFlags(FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED);
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(selectReportFilterForPicker(suffix));
  const selectedBrandId = reportFilters.brandFilter;
  const timePicker = useSelectDatePicker();
  const { startDate: start, endDate: end } = timePicker;
  const request = {
    report_type: ReportType.predictive_dashboard,
  };
  const showDemoData = useShowDemoData();
  const args: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [request],
    filters: [
      isMultiBrand && selectedBrandId
        ? [
            {
              field: 'brand_id',
              operator: 'equals',
              value: selectedBrandId,
            },
          ]
        : [],
    ],
    context: showDemoData ? BBKRequestContext.reports_filter : undefined,
  };
  const opts = {
    skip: !isPredictiveEnabled || !appKey,
    selectFromResult: selectFromResultTyped<PredictiveDashboardMetricsData>,
  };

  const {
    data: predictiveDashboardMetricsData,
    isLoading,
    isFetching,
    status,
  } = reportsSlice.endpoints.getReportV2.useQuery(args, opts);

  let metrics: PredictiveMetric[] =
    predictiveDashboardMetricsData?.metrics || [];
  const previousRefreshedAt =
    predictiveDashboardMetricsData?.previous_iteration_date || '';
  const previousRefreshedDate = moment.utc(previousRefreshedAt);
  let previousIterationDate = previousRefreshedDate.isValid()
    ? previousRefreshedDate.format('MM/DD/YYYY')
    : '';
  const currentRefreshedAt =
    predictiveDashboardMetricsData?.current_iteration_date || '';
  const currentRefreshedDate = moment.utc(currentRefreshedAt);
  let currentIterationDate = currentRefreshedDate.isValid()
    ? currentRefreshedDate.format('MM/DD/YYYY')
    : 'NA';
  if (status === QueryStatus.rejected) {
    metrics = [];
    currentIterationDate = 'NA';
    previousIterationDate = '';
  }
  const highRiskMetric: PredictiveMetric = metrics.find(
    (metric) => metric.churn_risk_category === RiskLevel.High.split(' ')[0]
  ) || {
    churn_risk_category: RiskLevel.High,
    average_churn_risk_score: { value: 0, change_percent: 0 },
    customer_count: { value: 0, change_percent: 0 },
    mrr_impact: { value: 0, change_percent: 0 },
  };

  const mediumRiskMetric: PredictiveMetric = metrics.find(
    (metric) => metric.churn_risk_category === RiskLevel.Medium.split(' ')[0]
  ) || {
    churn_risk_category: RiskLevel.Medium,
    average_churn_risk_score: { value: 0, change_percent: 0 },
    customer_count: { value: 0, change_percent: 0 },
    mrr_impact: { value: 0, change_percent: 0 },
  };

  const lowRiskMetric: PredictiveMetric = metrics.find(
    (metric) => metric.churn_risk_category === RiskLevel.Low.split(' ')[0]
  ) || {
    churn_risk_category: RiskLevel.Low,
    average_churn_risk_score: { value: 0, change_percent: 0 },
    customer_count: { value: 0, change_percent: 0 },
    mrr_impact: { value: 0, change_percent: 0 },
  };
  const highRiskCount = highRiskMetric.customer_count?.value || 0;
  const mediumRiskCount = mediumRiskMetric.customer_count?.value || 0;
  const lowRiskCount = lowRiskMetric.customer_count?.value || 0;

  let churnRiskRange = '';
  let currentMetric = highRiskMetric;
  const totalCustomers = highRiskCount + mediumRiskCount + lowRiskCount;
  const totalARR =
    highRiskMetric.mrr_impact.value +
    mediumRiskMetric.mrr_impact.value +
    lowRiskMetric.mrr_impact.value;

  let backgroundColor = 'tw-bg-white';
  let isHighCurrentRiskSegment = riskCategory === RiskLevel.High;
  let isMediumCurrentRiskSegment = riskCategory === RiskLevel.Medium;
  let isLowCurrentRiskSegment = riskCategory === RiskLevel.Low;

  if (riskCategory === RiskLevel.High) {
    backgroundColor = riskClassMap[RiskLevel.High];
    isHighCurrentRiskSegment = true;
    churnRiskRange = '70-100';
    currentMetric = highRiskMetric;
  } else if (riskCategory === RiskLevel.Medium) {
    backgroundColor = riskClassMap[RiskLevel.Medium];
    isMediumCurrentRiskSegment = true;
    currentMetric = mediumRiskMetric;
    churnRiskRange = '40-69';
  } else if (riskCategory === RiskLevel.Low) {
    backgroundColor = riskClassMap[RiskLevel.Low];
    isLowCurrentRiskSegment = true;
    churnRiskRange = '1-39';
    currentMetric = lowRiskMetric;
  }
  const churnRiskChange =
    currentMetric.average_churn_risk_score?.change_percent || 0;
  const atRiskCustomers = currentMetric.customer_count.value;
  const customerChange = currentMetric.customer_count.change_percent;
  const avgChurnRiskScore = currentMetric.average_churn_risk_score?.value || 0;

  const atRiskARR = currentMetric.mrr_impact.value;
  const arrChange = currentMetric.mrr_impact.change_percent;

  const history = useHistory();
  const createUrl = useUrl();

  // For growthV2, return to unified retention page; for legacy, return to old dashboard
  const returnUrl = growthV2License
    ? AuthorizedPaths.unifiedPlaysRetention
    : AuthorizedPaths.dashboard;

  const searchParams = {
    returnUrl,
    riskCategory: riskCategory,
  };

  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false);

  const navigateToPlay = () => {
    if (growthV2License) {
      // Open the create play modal for unified plays flow
      setShowCreatePlayModal(true);
    } else {
      // Legacy plays navigation
      history.push(
        createUrl(AuthorizedPaths.playNew) + encodeObjectForQuery(searchParams),
        {
          playType: agsLicense ? PlayType.RETENTION : PlayType.CHURN_PREVENTION,
          brand_id: selectedBrandId,
          riskCategory,
          name: riskCategory,
        }
      );
    }
  };
  const navigateToCustomerPreview = (path: string) => {
    history.push(createUrl(path) + encodeObjectForQuery(searchParams));
  };
  const [isHovered, setIsHovered] = useState(false);

  return (
    (deflectLicense || agsAndDeflectLicense) && (
      <div className="predictive-ai">
        <section className="tw-relative tw-flex">
          <div className="tw-flex tw-flex-col">
            <h3 className="tw-mb-2 tw-flex tw-w-full tw-items-center tw-text-2xl tw-font-bold [&_svg]:tw-mx-1">
              Growth AI {Icons.aiStar}
              <span className="tw-pl-1 tw-text-base">Churn Risk Levels</span>
            </h3>
            <p>
              Identify at-risk subscribers, and proactively take measures to
              retain them.{' '}
              <a
                href="https://www.chargebee.com/docs/growth/churn-prediction/ai-churn-scoring"
                target="_blank"
                rel="noreferrer"
                className="tw-text-cbrprimary-600 tw-underline"
              >
                Learn more
              </a>
            </p>
          </div>
          {isMultiBrand && (
            <div className="tw-absolute tw-right-1 tw-top-4 tw-flex tw-items-center">
              <PredictiveBrandFilterPicker />
            </div>
          )}
          <span className="tw-absolute tw-right-1 tw-top-16 tw-flex tw-items-end">
            Last refreshed at {currentIterationDate}
          </span>
        </section>
        <CardContentArea
          firstLoad={isLoading}
          loading={isFetching}
          customStyle={{ padding: 0 }}
        >
          <section className="tw-flex tw-flex-col tw-font-bold">
            <div className="tw-mt-3 tw-flex tw-w-full tw-flex-col tw-pb-4">
              <div className="tw-relative tw-flex tw-items-start tw-self-start tw-pb-2.5">
                <div
                  className={cs(
                    'tw-mr-3 tw-flex tw-flex-auto tw-gap-2 tw-text-sm',
                    {
                      'tw-z-10 tw-rounded tw-bg-cbrdanger-200':
                        isHighCurrentRiskSegment,
                    }
                  )}
                >
                  <RiskIndicator
                    type={RiskLevel.High}
                    count={highRiskCount}
                    showDownArrowFlag={isHighCurrentRiskSegment}
                    onClick={setRiskCategory}
                  />
                </div>
                <div
                  className={cs(
                    'tw-mr-3 tw-flex tw-flex-auto tw-gap-2 tw-text-sm',
                    {
                      'tw-z-10 tw-rounded tw-bg-cbrwarn-200':
                        isMediumCurrentRiskSegment,
                    }
                  )}
                >
                  <RiskIndicator
                    type={RiskLevel.Medium}
                    count={mediumRiskCount}
                    showDownArrowFlag={isMediumCurrentRiskSegment}
                    onClick={setRiskCategory}
                  />
                </div>
                <div
                  className={cs(
                    'tw-mr-3 tw-flex tw-flex-auto tw-gap-2 tw-text-sm',
                    {
                      'tw-z-10 tw-rounded tw-bg-cbryellow-200':
                        isLowCurrentRiskSegment,
                    }
                  )}
                >
                  <RiskIndicator
                    type={RiskLevel.Low}
                    count={lowRiskCount}
                    showDownArrowFlag={isLowCurrentRiskSegment}
                    onClick={setRiskCategory}
                  />
                </div>
              </div>
              <div
                className={cs(
                  'tw-relative tw-flex tw-justify-around',
                  'tw-rounded-lg tw-p-3',
                  backgroundColor
                )}
              >
                <MetricCard
                  title="Avg. churn risk score"
                  value={avgChurnRiskScore}
                  maxValue={100}
                  subtext={`(range ${churnRiskRange})`}
                  change={churnRiskChange}
                  icon={CBRIcons.churnProtectiveShield}
                  previousRefreshedDate={previousIterationDate}
                />
                <MetricCard
                  title="Customers"
                  value={atRiskCustomers}
                  maxValue={totalCustomers}
                  subtext={`(${totalCustomers === 0 ? '0%' : `${Math.round((atRiskCustomers / totalCustomers) * 100)}%`})`}
                  change={customerChange}
                  icon={Icons.peopleGroup}
                  previousRefreshedDate={previousIterationDate}
                />
                <MetricCard
                  title="ARR impact"
                  value={Math.round(atRiskARR)}
                  maxValue={Math.round(totalARR)}
                  subtext={`(${totalARR === 0 ? '0%' : `${Math.round((atRiskARR / totalARR) * 100)}%`})`}
                  change={arrChange}
                  icon={CBRIcons.dollarCoin}
                  isCurrency
                  previousRefreshedDate={previousIterationDate}
                />
                <div className="tw-my-auto tw-ml-2 tw-flex tw-basis-1/4 tw-flex-col tw-gap-1 tw-text-center tw-text-xs">
                  {(agsLicense || growthV2License) && (
                    <TippyTooltip
                      tippyOptions={{
                        placement: 'top',
                        disabled: selectedBrandId !== undefined && isMultiBrand,
                        visible:
                          isHovered &&
                          selectedBrandId === undefined &&
                          isMultiBrand,
                      }}
                      content={
                        selectedBrandId === undefined ? (
                          <span className="tw-font-normal">
                            View one brand to start targeting customers in a
                            play.{' '}
                          </span>
                        ) : null
                      }
                    >
                      <div
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      >
                        <SButton
                          className={cs(
                            'tw-flex tw-items-center tw-justify-center tw-gap-1 tw-overflow-hidden tw-rounded tw-px-4 tw-py-2.5',
                            {
                              'tw-pointer-events-none tw-opacity-50':
                                selectedBrandId === undefined && isMultiBrand,
                            }
                          )}
                          variant="primary"
                          onClick={navigateToPlay}
                        >
                          <span className="tw-my-auto tw-mr-1 tw-flex tw-aspect-square tw-w-4 tw-shrink-0 tw-self-stretch tw-object-contain">
                            {Icons.play}
                          </span>
                          <span
                            className={`tw-my-auto tw-flex tw-self-stretch ${
                              selectedBrandId === undefined && isMultiBrand
                                ? 'tw-opacity-50'
                                : ''
                            }`}
                          >
                            Target customers in a play
                          </span>
                        </SButton>
                      </div>
                    </TippyTooltip>
                  )}

                  <div className="tw-ml-4 tw-mt-4 tw-flex tw-flex-row tw-items-center tw-text-base tw-text-cbrprimary-800">
                    <span className="tw-mr-1 tw-pr-1">
                      {CBRIcons.threepeopleGroup}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        navigateToCustomerPreview(
                          AuthorizedPaths.viewPredictiveCustomerPreview
                        )
                      }
                      className="tw-text-cbrprimary-800 tw-underline"
                    >
                      View customer preview
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </CardContentArea>

        {growthV2License && (
          <CreatePlayModal
            isOpen={showCreatePlayModal}
            onClose={setShowCreatePlayModal}
            routePlayType="retention"
            additionalState={{
              riskCategory,
              name: riskCategory,
              returnTab: 'dashboard',
              returnUrl,
            }}
          />
        )}
      </div>
    )
  );
};
